import { useFieldArray, useForm } from "react-hook-form";
("use client");

import { AnimatePresence, motion } from "motion/react";

import EMRSectionSearch from "@/app/components/ui/Reusable/EMRSectionSearch";
import {
  EMRdetailsProps,
  IEMRSectionSearchResponseData,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import Input from "@/app/components/ui/Inputs/Input";
import { debounce, toNumber } from "lodash";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useStaticFrequencyAPI } from "@/app/hooks/api_hooks/emr/useStaticFrequencyAPI";
import { useStaticDurationAPI } from "@/app/hooks/api_hooks/emr/useStaticDurationAPI";
import { useStaticUnitsAPI } from "@/app/hooks/api_hooks/emr/useStaticUnitsAPI";
import { useStaticTimingsAPI } from "@/app/hooks/api_hooks/emr/useStaticTimingsAPI";
import { useStaticRoutesAPI } from "@/app/hooks/api_hooks/emr/useStaticRoutesAPI";
import InputWithSuggestionBox from "@/app/components/ui/Inputs/InputWithSuggestionBox";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";

import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import EMRItemsSuggestionsComponent from "../EMRItemsSuggestionsComponent";
import toast from "react-hot-toast";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import NestedTaperingDose from "../../CustomerProfile/EMRCreation/Sections/MedicationSection/NestedTaperingDose";
import { PlusIcon } from "lucide-react";
import { setTaperingIndex } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { LOCKED_CHILD_ITEM_IDS } from "@/constants";

type MedicationSectionFormFieldType = {
  medicine_id: string;
  name: string;
  dose?: string;
  dose_unit?: string;
  frequency?: string;
  timing?: string;
  route?: string;
  duration?: string;
  duration_unit?: string;
  remark?: string;
  quantity: number;
  is_child?: boolean;
  tapering_doses: Array<{
    dose?: string;
    dose_unit?: string;
    frequency?: string;
    duration?: string;
    duration_unit?: string;
    timing?: string;
    route?: string;
    remark?: string;
    quantity: number;
    is_child?: boolean;
  }>;
};

export interface IEMRMedicationSectionFormFields {
  medications: MedicationSectionFormFieldType[];
  instructions: string;
}

const MEDICINE_DROPDOWN_Z_INDEX = 20;

const isLockedChildMedicationItem = (item?: Partial<MedicationSectionFormFieldType>) =>
  Boolean(item?.is_child && item?.medicine_id && LOCKED_CHILD_ITEM_IDS.includes(item.medicine_id));

const MedicinesComponent = ({
  emrDetails,
  setEmrDetails,
  dropdownZIndex = MEDICINE_DROPDOWN_Z_INDEX,
}: EMRdetailsProps & { dropdownZIndex?: number }) => {
  const formHook = useForm<IEMRMedicationSectionFormFields>({
    mode: "onChange",
  });

  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { execute: logAPI } = useClickLogAPI();
  const dispatch = useAppDispatch();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const customerId = emrDetails?.customer_details?.customer_id;
  const pet_id = emrDetails?.pet_details?.pet_id;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: EMR_TEMPLATE_DELETE_LOCKED_ITEM — legacy role: super_admin
  const canDeleteLockedItem = useFeatureAccess("EMR_TEMPLATE_DELETE_LOCKED_ITEM");

  const { forNumericWithDecimal } = useFormValidations();
  const { data: frequencies, execute: fetchFrequencies } = useStaticFrequencyAPI();
  const { data: durations, execute: fetchDurations } = useStaticDurationAPI();
  const { data: units, execute: fetchUnits } = useStaticUnitsAPI();
  const { data: timings, execute: fetchTimings } = useStaticTimingsAPI();
  const { data: routes, execute: fetchRoutes } = useStaticRoutesAPI();

  const [animateLast, setAnimateLast] = useState(false);
  const [invalidQuantities, setInvalidQuantities] = useState<Record<number, boolean>>({});

  const doseRef = useRef<HTMLInputElement>(null);
  const frequencyRef = useRef<HTMLInputElement>(null);
  const timingRef = useRef<HTMLInputElement>(null);
  const durationUnitRef = useRef<HTMLInputElement>(null);

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const medicationFormHook = useFieldArray({
    name: "medications",
    control: formHook.control,
  });

  // True while the user has local edits that are not yet persisted. Set by
  // scheduleAutoSave/watch, cleared when the debounced save actually runs or
  // when a direct API call sends the current form values anyway.
  const pendingSaveRef = useRef(false);

  const prepareMedicationsForPayload = useCallback((medications: MedicationSectionFormFieldType[] = []) => {
    return (medications || []).map((medication) => {
      const parsedQuantity = toNumber(medication?.quantity);
      const safeQuantity =
        Number.isFinite(parsedQuantity) && parsedQuantity > 0
          ? Number.isInteger(parsedQuantity)
            ? parsedQuantity
            : Math.floor(parsedQuantity)
          : 1;

      return {
        ...medication,
        quantity: safeQuantity,
        tapering_doses: (medication?.tapering_doses || []).map((taperingDose) => {
          const parsedTaperingQuantity = toNumber(taperingDose?.quantity);
          const safeTaperingQuantity =
            Number.isFinite(parsedTaperingQuantity) && parsedTaperingQuantity > 0
              ? Number.isInteger(parsedTaperingQuantity)
                ? parsedTaperingQuantity
                : Math.floor(parsedTaperingQuantity)
              : 1;
          return {
            ...taperingDose,
            quantity: safeTaperingQuantity,
          };
        }),
      };
    });
  }, []);

  const debouncedUpdate = useMemo(
    () =>
      debounce((data: IEMRMedicationSectionFormFields) => {
        pendingSaveRef.current = false;
        const payload: IEMRUpdatePayloadProps = {
          medicines: {
            medicines_added: prepareMedicationsForPayload(data.medications),
            medicines_instructions: data.instructions,
          },
          remove_children: emrDetails?.remove_children,
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        if (JSON.stringify(data) === JSON.stringify(emrDetails?.medicines)) return;
        updateEmrAPI(emr_id, payload);
      }, 1000),
    [emr_id, emrDetails?.emr_status, prepareMedicationsForPayload],
  );

  // Quantity steppers, suggestion boxes (dose unit/frequency/timing/duration
  // unit) and tapering-dose fields mutate the form via setValue, which RHF
  // reports with type undefined — the watch filter below ignores those. Every
  // user-driven programmatic mutation must call this to persist.
  const scheduleAutoSave = () => {
    pendingSaveRef.current = true;
    debouncedUpdate(formHook.getValues());
  };

  const isFirstLoad = useRef(true);

  useEffect(() => {
    // Only auto-save on ACTUAL user input. RHF's watch callback signature is
    // (value, { name, type }) — `type` is defined ('change', 'blur', etc.) for
    // user events and undefined for programmatic setValue/reset/replace. Any
    // isFirstLoad timing dance is fragile because RHF doesn't guarantee that
    // the subscription callback fires synchronously with replace(); by the
    // time it fires, our setTimeout(0) has already flipped the flag back on.
    // Filtering on `type === "change"` is the deterministic fix.
    const subscription = formHook.watch((value, { type }) => {
      if (type !== "change") return;
      pendingSaveRef.current = true;
      debouncedUpdate(value as IEMRMedicationSectionFormFields);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  // The single guarded watch subscription above handles auto-save. A prior
  // implementation added another handleInputChange() that spun up a fresh
  // watch subscription on every keystroke without ever unsubscribing — those
  // orphaned subscriptions piled up and duplicated every subsequent API call.

  useEffect(() => {
    fetchFrequencies();
    fetchUnits();
    fetchTimings();
    fetchDurations();
    fetchRoutes();
  }, []);

  useEffect(() => {
    if (emrDetails) {
      // The user has unsaved local edits (e.g. a quantity change) and another
      // section's save just refreshed emrDetails with data that predates them.
      // Persist the local edits NOW and keep the form as-is — replacing it
      // here would silently reset quantities back to the stale server values.
      if (pendingSaveRef.current) {
        debouncedUpdate.flush();
        return;
      }

      isFirstLoad.current = false; // disable auto-save
      // Cancel any pending debounced update. Without this, a debounced call
      // that was scheduled from the user's own typing will still fire AFTER
      // the reset below, pushing stale form values back to the server.
      debouncedUpdate.cancel();

      const medicationItems = emrDetails?.medicines?.medicines_added || [];
      medicationFormHook.replace(
        medicationItems.map((item) => {
          if (isLockedChildMedicationItem(item) && toNumber(item?.quantity) <= 0) {
            return {
              ...item,
              quantity: 1,
            };
          }
          return item;
        }),
      );
      formHook.setValue("instructions", emrDetails?.medicines?.medicines_instructions || "", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });

      // enable watch only after state settled
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  const frequencyItems = useSelectMenuReducer(frequencies, "frequency_name", "frequency_name");
  const unitItems = useSelectMenuReducer(units, "dose_unit_name", "dose_unit_name");
  const timingsItems = useSelectMenuReducer(timings, "timing_name", "timing_name");
  const durationItems = useSelectMenuReducer(durations, "duration_name", "duration_name");

  const isChilderRemovefromEMR = (index: number, item: any) => {
    // This payload is built from getValues(), so any pending debounced edits
    // are already included — drop the pending save so the response replace runs.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    medicationFormHook.remove(index);
    const existing = formHook.getValues("medications");
    let remove_children = null;

    if (item?.is_child) {
      remove_children = [...(emrDetails?.remove_children ?? []), item?.medicine_id];
    } else {
      remove_children = [...(emrDetails?.remove_children ?? [])];
    }

    const payload: IEMRUpdatePayloadProps = {
      medicines: {
        medicines_added: prepareMedicationsForPayload(existing as MedicationSectionFormFieldType[]),
        medicines_instructions: formHook.getValues("instructions"),
      },
      remove_children: remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    callLogAPILabDeleted(item?.medicine_id);
  };

  const handleAddMedication = async (item: IEMRSectionSearchResponseData, source?: string) => {
    const existing = formHook.getValues("medications");

    const isPresent = medicationFormHook.fields.filter((f) => f.name === item.name);
    if (isPresent.length > 0) {
      toast("Medications already added.");
      return;
    }
    // Payload below includes pending edits via getValues(); cancel only after
    // the duplicate early-return so a rejected add keeps the pending save.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;

    const newItem = {
      name: item.name,
      tapering_doses: [],
      remark: "",
      medicine_id: item?.vetic_variant_id,
      quantity: 1,
      is_hd_stock: item?.is_hd_stock ?? undefined,
      prescription_type: item?.prescription_type
    };

    setAnimateLast(true);

    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      medicines: {
        medicines_added: prepareMedicationsForPayload(addedList as MedicationSectionFormFieldType[]),
        medicines_instructions: formHook.getValues("instructions"),
      },
      remove_children: emrDetails?.remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });

    source && callLogSuggestionAPI(item);
  };

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLast(false), 600);
    return () => clearTimeout(t);
  }, [animateLast, emrDetails]);

  const callLogAPIBase = (
    item: string,
    eventType: "EMR_QUANTITY_PLUS" | "EMR_QUANTITY_MINUS" | "EMR_ITEM_DELETED" | "SUGGESTION_ITEMS",
  ) => {
    const fieldValue = formHook.watch("medications");

    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: pet_id,
      click_on_search_item: item || undefined,
      medication_section: fieldValue || undefined,
      clinic_id: mappedClinicId,
      emr_search_type: "Medicines",
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  // Usage
  const callLogAPILab = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_PLUS");
  const callLogSuggestionAPI = (item: any) => callLogAPIBase(item, "SUGGESTION_ITEMS");
  const callLogAPILabMinus = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_MINUS");

  const callLogAPILabDeleted = (item: any) => {
    callLogAPIBase(item, "EMR_ITEM_DELETED");
  };

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <div className="bg-[#F9FAFB] rounded-xl p-2  ">
        <div className="flex flex-col gap-2 pb-2 sm:flex-row sm:gap-10 sm:items-center">
          <h3 className="shrink-0 text-[#000000] text-sm font-medium tracking-tight42 sm:w-2/12">Medicines</h3>
          <div className="w-full sm:flex-1 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddMedication(item);
              }}
              onFlexibleAdd={(itemName) => {
                if (itemName !== "") {
                  medicationFormHook.append({
                    name: itemName,
                    tapering_doses: [],
                    remark: "",
                    medicine_id: null,
                    quantity: 1,
                  });
                  scheduleAutoSave();
                }
              }}
              placeholder="Search & Add Medications"
              containerClassName="w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              flexibleAdd
              searchType="Medicines"
            />
          </div>
        </div>
        <div className="bg-white p-[10px] rounded-lg ">
          {medicationFormHook?.fields?.length > 0 && (
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-medium rounded-md">
                  <tr>
                  <th className="text-left text-[10px] sm:text-xs font-medium p-3 pl-2 w-72">
                    Item Names
                  </th>
                  <th className="text-left text-[10px] sm:text-xs font-medium pl-5">Dose</th>
                  <th
                    className="text-left text-[10px] sm:text-xs font-medium pl-3"
                  >
                    Frequency
                  </th>
                  <th className="text-left text-[10px] sm:text-xs font-medium pl-3">
                    Timing
                  </th>
                  <th
                    className="text-left text-[10px] sm:text-xs font-medium pl-5"
                  >
                    Duration
                  </th>
                  <th
                    className="text-left text-[10px] sm:text-xs font-medium pl-5"
                  >
                    Remark
                  </th>
                  <th
                    className="text-left text-[10px] sm:text-xs font-medium pl-5"
                  >
                    Quantity
                  </th>
                  <th className="text-left text-[10px] sm:text-xs font-medium">
                    Action
                  </th>
                  </tr>
                </thead>
                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {medicationFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === medicationFormHook?.fields?.length - 1;
                      const isSingle = medicationFormHook?.fields?.length === 1;
                      const isLockedChildItem = isLockedChildMedicationItem(field);
                      const isDeleteDisabled = isLockedChildItem && !canDeleteLockedItem;
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.medicine_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp = animateLast && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <>
                          <motion.tr
                            key={rowKey}
                            initial={initialProp}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.97 }}
                            // transition={{ duration: 0.28 }}
                            transition={{ duration: 0.45, ease: "easeInOut" }}
                            className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                          >
                            <td
                              // className={`p-2 text-left `}
                              className={`p-2 text-left ${isSingle
                                ? "rounded-tl-xl rounded-bl-xl"
                                : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-bl-xl" : ""}`.trim()
                                }`}
                            >
                              <div className="w-full relative">
                                <input hidden {...formHook.register(`medications.${index}.name`)} />
                                <div className="">
                                  <span className="text-[10px] sm:text-xs text-[#1F1F23] font-normal">
                                    {formHook.getValues(`medications.${index}.name`)}
                                  </span>
                                  {field?.is_child && (
                                    <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                      Suggested
                                    </p>
                                  )}
                                </div>
                                <div className="w-full flex justify-end items-center">
                                  <button
                                    data-tooltip-id={"tapering_dose_button"}
                                    data-tooltip-content="Add tapering dose"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      dispatch(setTaperingIndex(index));
                                    }}
                                    className="bg-gray-200 p-1 rounded-md text-gray-600 font-bold flex justify-center items-center flex-col bottom-0 top-full"
                                  >
                                    <PlusIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className={`text-left   w-56`}>
                              <div className="w-full pt-2 pb-2 pl-2">
                                <div className="relative rounded-md flex items-center gap-3 px-3 ">
                                  <Input
                                    variant="TRANSPARENT"
                                    type="number"
                                    errorMessages={[{ message: "", type: "required" }, forNumericWithDecimal.errors]}
                                    fieldError={
                                      formHook?.formState?.errors
                                        ? formHook?.formState?.errors?.medications
                                          ? formHook?.formState?.errors?.medications?.[index]
                                            ? formHook?.formState?.errors?.medications?.[index]?.dose
                                            : null
                                          : null
                                        : null
                                    }
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                    className="w-20 border-[#E2E2E2]"
                                    inputClassName="ring-1 focus:border-1 focus:ring-1 h-8 "
                                    placeholder={"e.g 10"}
                                    register={formHook.register(`medications.${index}.dose`, {
                                      required: false,
                                      ...forNumericWithDecimal.validations,
                                    })}
                                  />
                                  <div>
                                    <InputWithSuggestionBox
                                      dropdownZIndex={dropdownZIndex}
                                      showAllSuggestionsOnFocus={true}
                                      ref={doseRef}
                                      suggestions={unitItems.map((item) => item.title)}
                                      placeholder="Dose Unit"
                                      formErrors={!!formHook?.formState?.errors?.medications?.[index]?.dose_unit}
                                      defaultInputValue={formHook.getValues(`medications.${index}.dose_unit`)}
                                      onInputChange={(value) => {
                                        formHook.setValue(`medications.${index}.dose_unit`, value);
                                        scheduleAutoSave();
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={`text-left border-none  w-44`}>
                              <div className="pt-2 pb-2">
                                <div className="relative rounded-md">
                                  <div className="inset-y-0 right-0 flex">
                                    <div className="pl-3">
                                      <InputWithSuggestionBox
                                        dropdownZIndex={dropdownZIndex}
                                        showAllSuggestionsOnFocus={true}
                                        ref={frequencyRef}
                                        suggestions={frequencyItems.map((item) => item.title)}
                                        placeholder="Frequency"
                                        formErrors={!!formHook?.formState?.errors?.medications?.[index]?.frequency}
                                        defaultInputValue={formHook.getValues(`medications.${index}.frequency`)}
                                        onInputChange={(value) => {
                                          formHook.setValue(`medications.${index}.frequency`, value);
                                          scheduleAutoSave();
                                        }}
                                      // onKeyDown={(e) => handleKeyPress(e, timingRef)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={`text-left   "border-none" : "} w-44`}>
                              <div className="pt-2 pb-2">
                                <div>
                                  <div className="relative rounded-md">
                                    <div className="inset-y-0 right-0 flex">
                                      <div className="pl-3">
                                        <InputWithSuggestionBox
                                          dropdownZIndex={dropdownZIndex}
                                          showAllSuggestionsOnFocus={true}
                                          ref={timingRef}
                                          suggestions={timingsItems.map((item) => item.title)}
                                          placeholder="Timing"
                                          formErrors={false}
                                          defaultInputValue={formHook.getValues(`medications.${index}.timing`)}
                                          onInputChange={(value) => {
                                            formHook.setValue(`medications.${index}.timing`, value);
                                            scheduleAutoSave();
                                          }}
                                        // onKeyDown={(e) => handleKeyPress(e, durationUnitRef)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={`text-left  w-56`}>
                              <div className="pt-2 pb-2 pl-2">
                                <div className="">
                                  <div className="relative rounded-md flex gap-3  px-3">
                                    <Input
                                      variant="TRANSPARENT"
                                      type="number"
                                      errorMessages={[{ message: "", type: "required" }, forNumericWithDecimal.errors]}
                                      fieldError={
                                        formHook?.formState?.errors
                                          ? formHook?.formState?.errors?.medications
                                            ? formHook?.formState?.errors?.medications?.[index]
                                              ? formHook?.formState?.errors?.medications?.[index]?.duration
                                              : null
                                            : null
                                          : null
                                      }
                                      className="w-24 "
                                      inputClassName="ring-1 focus:border-1 focus:ring-1 h-8"
                                      placeholder={"e.g 5"}
                                      register={formHook.register(`medications.${index}.duration`, {
                                        required: false,
                                        ...forNumericWithDecimal.validations,
                                      })}
                                    />
                                    <div className="flex items-center">
                                      <InputWithSuggestionBox
                                        dropdownZIndex={dropdownZIndex}
                                        ref={durationUnitRef}
                                        suggestions={durationItems.map((item) => item.title)}
                                        placeholder="Duration Unit"
                                        formErrors={!!formHook?.formState?.errors?.medications?.[index]?.duration_unit}
                                        defaultInputValue={formHook.getValues(`medications.${index}.duration_unit`)}
                                        onInputChange={(value) => {
                                          formHook.setValue(`medications.${index}.duration_unit`, value);
                                          scheduleAutoSave();
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={`text-left   w-40`}>
                              <div className="w-full  ">
                                <Input
                                  errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                  fieldError={formHook.formState.errors.medications}
                                  register={formHook.register(`medications.${index}.remark`, {
                                    required: false,
                                  })}
                                  inputClassName="text-xs h-9"
                                  type="text"
                                  placeholder="Remarks"
                                />
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <div className="w-28 -mt-2">
                                <InputWithStartAndEndAddOn
                                  startAddon="-"
                                  startAddOnCallback={() => {
                                    const currentQuantity = toNumber(formHook.watch(`medications.${index}.quantity`));
                                    if (isLockedChildItem && currentQuantity <= 1) {
                                      formHook.setValue(`medications.${index}.quantity`, 1);
                                    } else if (currentQuantity !== 1) {
                                      formHook.setValue(
                                        `medications.${index}.quantity`,
                                        currentQuantity - 1,
                                      );
                                    }
                                    scheduleAutoSave();
                                    callLogAPILabMinus(formHook.watch(`medications.${index}.medicine_id`));
                                  }}
                                  step={1}
                                  endAddOnCallback={() => {
                                    formHook.setValue(
                                      `medications.${index}.quantity`,
                                      formHook.watch(`medications.${index}.quantity`) + 1,
                                    );
                                    scheduleAutoSave();
                                    callLogAPILab(formHook.watch(`medications.${index}.medicine_id`));
                                  }}
                                  min={1}
                                  endAddon="+"
                                  data-emr-quantity="true"
                                  data-invalid-quantity={invalidQuantities[index] ? "true" : "false"}
                                  value={formHook.watch(`medications.${index}.quantity`)}
                                  inputClassName={invalidQuantities[index] ? "!ring-red-500 !text-red-500" : ""}
                                  onChange={(e: any) => {
                                    const rawValue = e.target.value;
                                    if (rawValue === "") {
                                      formHook.setValue(`medications.${index}.quantity`, "" as any);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      return;
                                    }
                                    const parsedQuantity = toNumber(rawValue);
                                    if (!Number.isNaN(parsedQuantity)) {
                                      const safeQuantity = isLockedChildItem && parsedQuantity <= 0 ? 1 : parsedQuantity;
                                      formHook.setValue(`medications.${index}.quantity`, safeQuantity as any);
                                      setInvalidQuantities((prev) => ({
                                        ...prev,
                                        [index]: safeQuantity < 0 || !Number.isInteger(safeQuantity),
                                      }));
                                      if (safeQuantity > 0 && Number.isInteger(safeQuantity)) {
                                        scheduleAutoSave();
                                      }
                                    }
                                    callLogAPILab(formHook.watch(`medications.${index}.medicine_id`));
                                  }}
                                  onBlur={() => {
                                    const currentValue: any = formHook.getValues(`medications.${index}.quantity`);
                                    const parsedQuantity = toNumber(currentValue);

                                    if (currentValue === "" || Number.isNaN(parsedQuantity)) {
                                      formHook.setValue(`medications.${index}.quantity`, 1);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      scheduleAutoSave();
                                      return;
                                    }

                                    if (parsedQuantity <= 0) {
                                      formHook.setValue(`medications.${index}.quantity`, 1);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      scheduleAutoSave();
                                      if (!isLockedChildItem) {
                                        toast.error("Decimal, Zero and Negative values are not allowed !");
                                      }
                                      return;
                                    }

                                    if (!Number.isInteger(parsedQuantity)) {
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: true }));
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                      return;
                                    }

                                    setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                  }}
                                  type="number"
                                />
                              </div>
                            </td>

                            <td
                              className={`p-2 text-center ${isSingle
                                ? "rounded-tr-xl rounded-br-xl"
                                : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                                }`}
                            >
                              <button
                                className={`w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ${isDeleteDisabled ? "opacity-50 cursor-not-allowed" : ""
                                  }`}
                                disabled={isDeleteDisabled}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isDeleteDisabled) {
                                    return;
                                  }
                                  isChilderRemovefromEMR(index, field);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="white"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-5 text-[#ED0101]"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                  />
                                </svg>
                              </button>
                            </td>
                          </motion.tr>
                          <NestedTaperingDose
                            durations={durations}
                            frequencies={frequencies}
                            routes={routes}
                            timings={timings}
                            units={units}
                            mode={"EDIT"}
                            formHook={formHook}
                            nestedIndex={index}
                            onProgrammaticEdit={scheduleAutoSave}
                            dropdownZIndex={dropdownZIndex}
                          />
                        </>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
          <EMRItemsSuggestionsComponent
            handelCallback={(item, s) => handleAddMedication(item, s)}
            suggestionData={emrDetails?.medicines?.medicines_suggestions}
          />
        </div>

        <div className="bg-[#FFFFFF] rounded-xl p-[10px]  mt-2">
          <textarea
            className="w-full   border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
            placeholder="Additional Medication Instructions"
            {...formHook.register("instructions", {
              required: false,
            })}
          ></textarea>
        </div>
      </div>
    </div>
  );
};
export default MedicinesComponent;

// medicines_suggestions
